import { type MouseEvent, SyntheticEvent } from 'react';

/**
 * A no-operation function.
 */
export const noop = () => {};

/**
 * Prevents an event from propagating and performing its default action.
 *
 * @param {SyntheticEvent} evt - The synthetic event to be handled.
 */
export const preventClickThrough = (evt: SyntheticEvent) => {
  evt.preventDefault();
  evt.stopPropagation();
};

export const isModifiedClick = (e: MouseEvent) =>
  e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

export const onPlainClick = (action: () => void) => (e: MouseEvent) => {
  if (isModifiedClick(e)) {
    return;
  }
  e.preventDefault();
  action();
};

interface KeyboardEvent<T = Element> extends SyntheticEvent<T> {
  code: string;
  altKey: boolean;
  charCode: number;
  ctrlKey: boolean;
  getModifierState(key: string): boolean;
  key: string;
  keyCode: number;
  locale: string;
  location: number;
  metaKey: boolean;
  repeat: boolean;
  shiftKey: boolean;
  which: number;
}

/**
 * Checks if a key event corresponds to the Enter key.
 *
 * @param {KeyboardEvent} e - The keyboard event.
 * @returns {boolean} - Returns true if the Enter key is pressed.
 */
export const isKeyEnter = (e: KeyboardEvent) => e.key === 'Enter';

/**
 * Checks if a key event corresponds to the Tab key.
 *
 * @param {KeyboardEvent} e - The keyboard event.
 * @returns {boolean} - Returns true if the Tab key is pressed.
 */
export const isKeyTab = (e: KeyboardEvent) => e.key === 'Tab';

/**
 * Whether an Enter press inside a dialog should trigger its primary action. Shift+Enter,
 * textareas and floating poppers (combobox, select, date picker) own the key themselves.
 */
export const isDialogSubmitKey = (e: KeyboardEvent) => {
  if (!isKeyEnter(e) || e.shiftKey) {
    return false;
  }
  const target = e.target as HTMLElement | null;
  return !target?.closest?.('textarea, [data-radix-popper-content-wrapper]');
};
